import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { App as AntApp, ConfigProvider, theme as antTheme } from "antd";
import { ReactElement } from "react";
import { IconContext } from "react-icons/lib";
import { Provider } from "react-redux";

import store from "store";
import { grayColours, PROJECT_INFO } from "utils/constants";
import { queryClient } from "utils/queryClient";
import { useTheme } from "utils/theme";
/**  ******* NOTE ******** dayjs plugins - uncomment as necessary */
import dayjs from "dayjs";
// import advancedFormat from "dayjs/plugin/advancedFormat";
// import isToday from "dayjs/plugin/isToday";
// import isYesterday from "dayjs/plugin/isYesterday";
// import localeData from "dayjs/plugin/localeData";
import relativeTime from "dayjs/plugin/relativeTime";

function App({ children }: { children: ReactElement }) {
  const { colorScheme } = useTheme();

  // dayjs.extend(localeData);
  // dayjs.extend(isYesterday);
  // dayjs.extend(isToday);
  dayjs.extend(relativeTime);
  // dayjs.extend(advancedFormat);

  return (
    <QueryClientProvider client={queryClient}>
      {/* <ReactQueryDevtsools initialIsOpen={false} /> */}
      <IconContext.Provider value={{ style: { verticalAlign: "middle" } }}>
        <Provider store={store}>
          <ConfigProvider
            theme={{
              algorithm: colorScheme === "dark" ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
              token: {
                colorPrimary: PROJECT_INFO.colors.primary,
                colorLink: PROJECT_INFO.colors.link,
                // colorText: "#334155",
                borderRadius: 6,
                fontFamily: "'Inter', sans-serif",
                // fontSize: 16,
                colorTextDescription: grayColours[500],
                colorTextDisabled: "#6b7280",
                colorInfo: "#3b82f6",
                colorSuccess: "#22c55e",
                colorWarning: "#facc15",
                colorError: "#f41841",
                wireframe: false,
                colorBgBase: "#ffffff",
                colorBgLayout: "#ffffff",
                colorBgContainerDisabled: "#f9fafb",
                colorTextBase: "#374151",
                // borderRadius: 8,
                borderRadiusLG: 12,
                // colorLink: "#a855f7",
                colorBorder: "#d1d5db",
                colorBorderSecondary: "#f3f4f6",
                // colorBgLayout: "#f9fafb",

                colorBgMask: "#1f293773",
                fontSizeSM: 13,
                lineHeight: 1.3,

                blue: "#3b82f6",
                green: "#22c55e",
                red: "#ef4444",
                yellow: "#facc15",
                // red1: "#fef2f2",
                // red2: "#fecaca",
                // red3: "#fca5a5",
                // red4: "#f87171",
                // red5: "#ef4444",
                // red6: "#dc2626",
                // red7: "#b91c1c",
                // red8: "#991b1b",
                // red9: "#7f1d1d",
                // red10: "#450a0a",
                colorTextPlaceholder: "#9ca3af",
                colorIcon: "#9ca3af",
                colorBgSpotlight: "#030712",
              },
              components: {
                // Layout: {
                //   // bodyBg: "#f8fafc",
                //   headerBg: "white",
                //   siderBg: PROJECT_INFO.colors.darkBg,
                //   triggerBg: PROJECT_INFO.colors.darkBg,
                //   headerHeight: PROJECT_INFO.layout.headerHeight,
                // },
                // Menu: {
                //   darkItemBg: PROJECT_INFO.colors.darkBg,
                // },
                // Button: {
                //   primaryColor: PROJECT_INFO.colors.link,
                // },

                Button: {
                  fontWeight: 500,
                  contentLineHeightSM: 1.6,
                  defaultBorderColor: "rgb(209,213,219)",
                  borderColorDisabled: "rgb(209,213,219)",
                  contentLineHeight: 1.5,
                  controlHeight: 36,
                  paddingInline: 16,
                },
                Typography: {
                  colorTextHeading: "rgb(31,41,55)",
                  colorTextDescription: "rgb(107,114,128)",
                  colorText: "rgb(55,65,81)",
                  colorTextDisabled: "rgb(156,163,175)",
                  fontFamilyCode: "system-ui, 'Inter', Consolas, 'Liberation Mono', Menlo, Courier, monospace",
                },
                Divider: {
                  colorSplit: "rgb(229,231,235)",
                  colorText: "rgb(107,114,128)",
                },
                Layout: {
                  headerBg: "rgb(255,255,255)",
                  // headerColor: "rgba(255,255,255,0.88)",
                  siderBg: "rgb(255,255,255)",
                  bodyBg: "rgb(243,244,246)",
                  footerBg: "rgb(243,244,246)",
                  lightTriggerColor: "rgb(0,33,64)",
                  headerHeight: PROJECT_INFO.layout.headerHeight,
                },
                Menu: {
                  itemBorderRadius: 6,
                },
                Checkbox: {
                  colorBorder: "rgb(209,213,219)",
                  controlHeight: 36,
                },
                Form: {
                  controlHeight: 36,
                  labelFontSize: 13.5,
                },
                Input: {
                  controlHeight: 36,
                },
                InputNumber: {
                  controlHeight: 36,
                },
                Select: {
                  controlHeight: 36,
                },
                Segmented: {
                  trackBg: grayColours[100],
                  padding: 4,
                },
                Table: {
                  borderColor: "#e5e7eb",
                },
                Tooltip: {
                  fontSize: 12,
                  controlHeight: 28,
                },
                Splitter: {
                  splitBarSize: 6,
                },
              },
              cssVar: true,
              hashed: false,
            }}
          >
            <AntApp>{children}</AntApp>
          </ConfigProvider>
        </Provider>
      </IconContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
